import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FeatureTogglesModule } from '@cbre/feature-toggles';
import { CbreHttpModule } from '@cbre/http';
import { DirectivesModule, FormattingModule, TranslationModule } from '@miq/utils';
import { L10nTranslationModule } from 'angular-l10n';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TreeModule } from 'primeng/tree';
import { FiltersToolbarComponent } from './filters-toolbar/filters-toolbar.component';
import { GetDateString } from './shared/pipes/date.pipe';
import { UpdateUnaccountedFilterPipe } from './shared/pipes/update-unaccounted-filter.pipe';
import { ActionsToolbarComponent } from './stacking-plan-container/actions-toolbar/actions-toolbar.component';
import { ExportComponent } from './stacking-plan-container/actions-toolbar/export/export.component';
import { SettingComponent } from './stacking-plan-container/actions-toolbar/settings/settings.component';
import { ConfirmDialogSPComponent } from './stacking-plan-container/dialogs/confirm-dialog-sp/confirm-dialog-sp.component';
import { ElevatorsComponent } from './stacking-plan-container/dialogs/more-info-dialog/elevators/elevators.component';
import {
  IsFloorIndexEqualToInvalidIndexValuePipe,
  IsFloorIndexValidPipe,
} from './stacking-plan-container/dialogs/more-info-dialog/floors/floor-index-valid.pipe';
import { FloorsActionsComponent } from './stacking-plan-container/dialogs/more-info-dialog/floors/floors-actions/floors-actions.component';
import { FloorsComponent } from './stacking-plan-container/dialogs/more-info-dialog/floors/floors.component';
import { GetFloorFGPipe } from './stacking-plan-container/dialogs/more-info-dialog/floors/get-floor-fc.pipe';
import { MoveFloorComponent } from './stacking-plan-container/dialogs/more-info-dialog/floors/move-floor/move-floor.component';
import { DeleteFloorSelectedComponent } from './stacking-plan-container/dialogs/more-info-dialog/floors/delete-floor-selected/delete-floor-selected.component';
import { DisableInput, ResultList } from './stacking-plan-container/dialogs/more-info-dialog/key-value.pipe';
import { MoreInfoDialogComponent } from './stacking-plan-container/dialogs/more-info-dialog/more-info-dialog.component';
import { NonRentableSpaceComponent } from './stacking-plan-container/dialogs/non-rentable-space/non-rentable-space.component';
import { PendingIssuesComponent } from './stacking-plan-container/dialogs/pending-issues/pending-issues.component';
import { ElevatorBanksComponent } from './stacking-plan-container/floor-container/elevator-banks/elevator-banks.component';
import { FloorContainerComponent } from './stacking-plan-container/floor-container/floor-container.component';
import { FloorComponent } from './stacking-plan-container/floor-container/floor/floor.component';
import { DataFormatterPipe } from './stacking-plan-container/floor-container/floor/item/details-overlay/data-formatter.pipe';
import { LabelFormatterPipe } from './stacking-plan-container/floor-container/floor/item/details-overlay/label-formatter.pipe';
import { DetailsOverlayComponent } from './stacking-plan-container/floor-container/floor/item/details-overlay/details-overlay.component';
import { InputFieldsComponent } from './stacking-plan-container/floor-container/floor/item/details-overlay/input-fields/input-fields.component';
import { NaicsInputComponent } from './stacking-plan-container/floor-container/floor/item/details-overlay/input-fields/record-naics-input/naics-input.component';
import { IsRequired } from './stacking-plan-container/floor-container/floor/item/details-overlay/mandatory-check.directive';
import { ItemComponent } from './stacking-plan-container/floor-container/floor/item/item.component';
import { OverlayService } from './stacking-plan-container/floor-container/floor/item/overlay.service';
import { StackingPlanContainerComponent } from './stacking-plan-container/stacking-plan-container.component';
import { StackingPlanComponent } from './stacking-plan.component';
import {
  INFO_USER_RESOLVER_TOKEN,
  OVERLAY_UTILS_TOKEN,
  OverlayUtilsConfiguration,
  PV_SIDE_BY_SIDE_TEMPLATES_TOKEN,
  PvSideBySideTemplates,
  STACKING_PLAN_CONFIGS_TOKEN,
  StackingPlanConfiguration,
} from './stacking-plan.token';
import { ThemeDirective } from './theme.directive';
import { FormErrorMessageDirective } from './stacking-plan-container/floor-container/floor/item/details-overlay/form-group-error-message.directive';
import { OpportunityComponent } from './stacking-plan-container/floor-container/floor/item/details-overlay/opportunity/opportunity.component';
import { LastReviewedByComponent } from './last-reviewed-by/last-reviewed-by.component';
import { AddLastReviewedComponent } from './last-reviewed-by/add-last-reviewed/add-last-reviewed.component';
import { MatFormFieldModule as MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DetailsModalComponent } from './stacking-plan-container/floor-container/floor/item/details-modal/details-modal.component';
import { PropertyViewComponent } from './property-view/property-view.component';
import { VerticalSegmentsComponent, TableComponent, SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { PropertyViewElevatorComponent } from './property-view/property-elevator/property-elevator.component';
import { FloorRendererComponent } from './property-view/floor-renderer/floor-renderer.component';
import { ToolbarComponent } from './stacking-plan-container/toolbar/toolbar.component';
import { MiqCatalogModule } from '@miq/catalog';
import { CellRenderComponent } from './property-view/floor-info/cell-render';
import { BuildingSummaryComponent } from './property-view/building-summary/building-summary.component';
import { ExportExecutableDirective } from './stacking-plan-container/actions-toolbar/export/export-executable.directive';
import { UpdateBuildingExecutableService } from './stacking-plan-container/actions-toolbar/building-info/update-building-executable.service';
import { UpdateBuildingExecutableDirective } from './stacking-plan-container/actions-toolbar/building-info/update-building-executable.directive';
import { FloorClassAdapter } from './stacking-plan-container/floor-container/floor-container-adapter.service';
import { FloorContainerRepository } from './stacking-plan-container/floor-container/floor-container-repository.service';
import { StackingPlanService } from './stacking-plan.service';
import { ElevatorBanksRepository } from './stacking-plan-container/floor-container/elevator-banks/elevator-banks-repository.service';
import { ExportExecutableService } from './stacking-plan-container/actions-toolbar/export/export-executable.service';
import { PVFloorSpaceDetailsComponent } from './property-view/floor-space-details/floor-space-details.component';
import { StackPlanViewStateService } from './state/sp-view-state.service';
import { SettingsExecutableDirective } from './stacking-plan-container/actions-toolbar/settings/settings-executable.directive';
import { SettingsExecutableService } from './stacking-plan-container/actions-toolbar/settings/settings-executable.service';
import { SharedPipesModule } from '@miq/utils/pipes';
import { PVTenantSummaryComponent } from './property-view/floor-space-details/tenant-summary/tenant-summary.component';
import { PVSpacesDetailsComponent } from './property-view/floor-space-details/spaces-details/spaces-details.component';

@NgModule({
  declarations: [
    StackingPlanComponent,
    StackingPlanContainerComponent,
    FloorComponent,
    ItemComponent,
    DetailsOverlayComponent,
    OpportunityComponent,
    InputFieldsComponent,
    NaicsInputComponent,
    ActionsToolbarComponent,
    FiltersToolbarComponent,
    FloorContainerComponent,
    ElevatorBanksComponent,
    GetDateString,
    ThemeDirective,
    MoreInfoDialogComponent,
    FloorsComponent,
    MoveFloorComponent,
    DeleteFloorSelectedComponent,
    ElevatorsComponent,
    SettingComponent,
    ResultList,
    ExportComponent,
    NonRentableSpaceComponent,
    PendingIssuesComponent,
    DataFormatterPipe,
    LabelFormatterPipe,
    DisableInput,
    GetFloorFGPipe,
    IsFloorIndexValidPipe,
    IsFloorIndexEqualToInvalidIndexValuePipe,
    FloorsActionsComponent,
    IsRequired,
    ConfirmDialogSPComponent,
    UpdateUnaccountedFilterPipe,
    FormErrorMessageDirective,
    LastReviewedByComponent,
    AddLastReviewedComponent,
    DetailsModalComponent,
    PropertyViewComponent,
    FloorRendererComponent,
    PropertyViewElevatorComponent,
    CellRenderComponent,
    BuildingSummaryComponent,
    ExportExecutableDirective,
    UpdateBuildingExecutableDirective,
    SettingsExecutableDirective,
    PVFloorSpaceDetailsComponent,
    PVTenantSummaryComponent,
    PVSpacesDetailsComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    TableModule,
    FormsModule,
    OverlayModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    MatTabsModule,
    MatSnackBarModule,
    MatDialogModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDatepickerModule,
    PaginatorModule,
    CbreHttpModule,
    FeatureTogglesModule.forChild(),
    FormattingModule,
    TranslationModule,
    L10nTranslationModule,
    MatProgressSpinnerModule,
    OverlayPanelModule,
    TreeModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    VerticalSegmentsComponent,
    ToolbarComponent,
    MiqCatalogModule,
    DirectivesModule,
    TableComponent,
    SharedPipesModule,
    SpinnerLoadingIndicatorComponent,
  ],
  exports: [StackingPlanComponent],
})
/**
 * Stacking Plan Module must be imported using the static forRoot method, which
 * requires an object that follows the {@link StackingPlanConfiguration}
 * interface
 */
export class StackingPlanModule {
  /**
   * Receives a {@link StackingPlanConfiguration} object and injects it into
   * the module as a provider using the {@link STACKING_PLAN_CONFIGS_TOKEN} as
   * the injection token.
   */
  static forRoot(
    options: StackingPlanConfiguration = { baseUrl: '', media: {} },
    overlayUtils: OverlayUtilsConfiguration,
    userInfoResolver: any,
    pvSideBySideTemplates: PvSideBySideTemplates
  ): ModuleWithProviders<StackingPlanModule> {
    return {
      ngModule: StackingPlanModule,
      providers: [
        { provide: STACKING_PLAN_CONFIGS_TOKEN, useValue: options },
        { provide: OVERLAY_UTILS_TOKEN, useValue: overlayUtils },
        { provide: INFO_USER_RESOLVER_TOKEN, useClass: userInfoResolver },
        { provide: PV_SIDE_BY_SIDE_TEMPLATES_TOKEN, useValue: pvSideBySideTemplates },
        OverlayService,
        UpdateBuildingExecutableService,
        SettingsExecutableService,
        FloorClassAdapter,
        ElevatorBanksRepository,
        FloorContainerRepository,
        StackingPlanService,
        StackPlanViewStateService,
        ExportExecutableService,
      ],
    };
  }
}
